export const normalizeAppliedVouchers = (data) => {
  if (Array.isArray(data)) {
    return data.filter(Boolean);
  }
  if (data && typeof data === "object") {
    return [data];
  }
  return [];
};

/**
 * Invoice may contain leftover join rows from failed payment retries.
 * Keep only vouchers that match the sale voucher_discount (prefer newest rows).
 */
export const resolveInvoiceAppliedVouchers = (appliedVouchers = [], voucherDiscount = 0) => {
  const rows = normalizeAppliedVouchers(appliedVouchers);
  if (!rows.length) return [];

  const target = parseFloat(voucherDiscount) || 0;
  if (target <= 0) return rows;

  const total = rows.reduce((sum, row) => sum + (parseFloat(row?.discount_amount) || 0), 0);
  if (Math.abs(total - target) < 0.01) return rows;

  const sortedNewestFirst = [...rows].sort((a, b) => (Number(b?.id) || 0) - (Number(a?.id) || 0));
  const selected = [];
  let sum = 0;

  for (const row of sortedNewestFirst) {
    const amount = parseFloat(row?.discount_amount) || 0;
    if (amount <= 0) continue;
    if (sum + amount > target + 0.01) continue;
    selected.push(row);
    sum += amount;
    if (Math.abs(sum - target) < 0.01) break;
  }

  return selected.length ? selected.reverse() : rows;
};

export const getVoucherServiceId = (voucher) =>
  voucher?.voucher?.service?.id || voucher?.service?.id || voucher?.service_id || null;

export const voucherCoversService = (voucher, serviceId) => {
  const voucherServiceId = getVoucherServiceId(voucher);
  return Boolean(voucherServiceId && serviceId && String(voucherServiceId) === String(serviceId));
};

export const anyVoucherCoversService = (vouchers, serviceId) =>
  normalizeAppliedVouchers(vouchers).some((voucher) => voucherCoversService(voucher, serviceId));

export const collectCheckoutServiceIds = ({ appointmentDetail, isCart } = {}) => {
  const ids = [];
  if (appointmentDetail?.service_id) {
    ids.push(appointmentDetail.service_id);
  }
  (appointmentDetail?.addonservices || []).forEach((item) => {
    if (item?.service_id) ids.push(item.service_id);
  });
  (appointmentDetail?.addanotherservices || []).forEach((item) => {
    if (item?.service_id) ids.push(item.service_id);
  });
  (isCart?.services || []).forEach((service) => {
    if (service?.id) ids.push(service.id);
  });
  (isCart?.appointments || []).forEach((appointment) => {
    if (appointment?.service_id) ids.push(appointment.service_id);
    (appointment?.addonservices || []).forEach((item) => {
      if (item?.id) ids.push(item.id);
      if (item?.service_id) ids.push(item.service_id);
    });
    (appointment?.addanotherservices || []).forEach((item) => {
      if (item?.id) ids.push(item.id);
      if (item?.service_id) ids.push(item.service_id);
    });
  });
  return [...new Set(ids.map(String))];
};

/**
 * Waterfall voucher discounts across multiple gift vouchers.
 * Amount vouchers consume remaining_balance; service vouchers consume amount when the service is in checkout.
 */
export const calculateAppliedVoucherDiscounts = (vouchers, totalPay, checkoutServiceIds = []) => {
  const appliedVouchers = normalizeAppliedVouchers(vouchers);
  const serviceIdSet = new Set((checkoutServiceIds || []).map(String));
  let remaining = Math.max(0, parseFloat(totalPay) || 0);
  let totalDiscount = 0;
  const lines = [];
  const usedServiceIds = new Set();

  appliedVouchers.forEach((voucher) => {
    const serviceId = getVoucherServiceId(voucher);
    const isServiceType = voucher?.is_active_voucher_type === "service" && serviceId;
    let discount = 0;

    if (remaining > 0) {
      if (isServiceType) {
        const serviceKey = String(serviceId);
        if (serviceIdSet.has(serviceKey) && !usedServiceIds.has(serviceKey)) {
          discount = Math.min(parseFloat(voucher.amount) || 0, remaining);
          usedServiceIds.add(serviceKey);
        }
      } else {
        const balance = parseFloat(voucher.remaining_balance) || 0;
        discount = Math.min(balance, remaining);
      }
    }

    discount = parseFloat(parseFloat(discount).toFixed(2));
    lines.push({
      voucher_to_id: voucher.id,
      discount,
      voucher,
    });
    totalDiscount = parseFloat((totalDiscount + discount).toFixed(2));
    remaining = parseFloat(Math.max(0, remaining - discount).toFixed(2));
  });

  return {
    lines,
    totalDiscount,
    remainingPay: remaining,
    voucher_to_id: lines[0]?.voucher_to_id || "",
    voucher_to_ids: lines.map((line) => line.voucher_to_id),
  };
};
